<template>
  <div
    ref="wrapper"
    class="main-collection critical-component-clear"
    :class="`section-content--${contentColor}`"
    :style="`--collection-bg: ${backgroundColor};`"
  >
    <private-collection
      v-if="locked"
      @dismissed="hideCollection = true"
    />
    <bread-crumbs
      v-if="!collection.hideBreadcrumbs"
      :merchant="section.merchant"
      :collection="collection"
      class="main-collection__breadcrumbs is-hidden--desktop"
    />
    <!-- Mobile Filter Tabs -->
    <filter-tabs
      v-if="isMobile"
      content-class="col xs4"
      block-class="main-collection"
      :tab-title="collection.title"
      :hide-title="collection.hideTitle"
      :use-filters="!collection.hideProductGrid"
      :selected-filters-count="selectedFilters.length"
      @show-filters="openFilters"
      @layout-toggled="toggleLayout"
    />

    <!-- Collection header -->
    <div
      v-if="!collection.hideHeader"
      class="main-collection__header"
      :class="[
        filterContentColour,
        {
          'bg--transparent': isMobile && tabExpanded
        },
      ]"
      :style="`--collection-bg: ${filterBackground};`"
    >
      <div class="grid no-row-gap">
        <div
          v-if="collection.metaTitleDesktop && !isMobile"
          class="
              main-collection__meta-title
              col
              l12
              text-desktop-32-title-uc-desktop
            "
          v-html="collection.metaTitleDesktop"
        />

        <div
          v-if="collection.metaTitleMobile && isMobile"
          class="
              main-collection__meta-title
              col
              xs4
              text-mobile-24-title-uc-mobile
            "
          v-html="collection.metaTitleMobile"
        />

        <div
          class="col l3 is-hidden--mobile"
        >
          <h1
            v-if="!collection.hideTitle"
            class="main-collection__title text-desktop-medium text-regular"
            v-text="collection.title"
          />
        </div>

        <div
          v-if="collection.navigation.items.length > 0"
          class="
          main-collection__navigation
          col
          l3
          text-desktop-regular"
        >
          <ul
            ref="navigation"
            class="main-collection__navigation-list"
          >
            <li
              v-for="item of collection.navigation.items"
              :key="item.handle"
              class="
                main-collection__navigation-item
                text-mobile-16-mobile
                text-desktop-16-desktop
              "
            >
              <a
                class="
                  main-collection__navigation-link
                "
                :href="item.url"
                v-text="item.title"
              />
            </li>
          </ul>
        </div>

        <!-- eslint-disable max-len -->
        <div
          v-if="!collection.hideDescription && collection.description"
          class="
            main-collection__description-container
            col
            xs4
            l4
            text-desktop-regular"

          :class="`main-collection__description-container--mobile-${collection.descriptionAlignmentMobile}`"

          v-html="collection.description"
        />
        <!-- eslint-enable max-len -->

        <!-- Desktop filters -->
        <teleport to="body">
          <!-- Filter blur overlay -->
          <overlay
            key="collectionFilters"
            block-class="collection-filters-overlay"
            direction="left"
            namespace="CollectionFilters"
            type="drawer"
          >
            <template #title>
              <span v-text="$string('collection.filter_tabs_title')" />
            </template>

            <template #body>
              <div class="main-collection__filters-container">
                <div class="main-collection__filters">
                  <div class="main-collection__filters-body">
                    <div
                      v-for="filter of filters"
                      :key="filter.key"
                      class="main-collection__filter-container"
                    >
                      <p
                        class="main-collection__filter-title text-desktop-small"
                        v-text="filter.name"
                      />

                      <filter-list
                        :ref="filter.key"
                        block-class="main-collection"
                        :filter="filter"
                        :is-selected="filterContainsSelected(filter.key)"
                        @filter-selected="handleFilterSelect"
                      />
                    </div>
                  </div>

                  <div class="main-collection__filters-footer">
                    <button
                      v-if="selectedFilters.length"
                      type="button"
                      class="button button--block main-collection__filter-reset"
                      @click="resetAllFilters"
                      v-text="$string('search.reset')"
                    />

                    <button
                      type="button"
                      class="button button--block button--secondary main-collection__filter-submit"
                      @click="openFilters"
                    >
                      <span
                        class="main-collection__filter-text"
                        v-text="tabTitle"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </overlay>
        </teleport>
      </div>
    </div>

    <!-- Desktop controls -->
    <div
      v-if="!collection.hideProductGrid"
      class="main-collection__controls grid is-hidden--mobile"
    >
      <bread-crumbs
        v-if="!collection.hideBreadcrumbs"
        :merchant="section.merchant"
        :collection="collection"
        class="main-collection__breadcrumbs is-hidden--mobile col l6"
      />
      <div
        v-if="!collection.hideProductGrid"
        class="main-collection__layout col l3"
      >
        <span
          class="main-collection__layout-text text-desktop-regular-desktop
                text-mobile-regular-mobile"
          v-text="layoutText"
        />

        <button
          :aria-label="$string('accessibility.collection.change_layout')"
          type="button"
          class="main-collection__layout-toggle text-desktop-regular"
          :class="currentLayout === 2 && 'main-collection__layout-toggle--active'"
          @click="toggleLayout(2)"
          v-text="2"
        />

        <button
          :aria-label="$string('accessibility.collection.change_layout')"
          type="button"
          class="main-collection__layout-toggle text-desktop-regular"
          :class="currentLayout === 0 && 'main-collection__layout-toggle--active'"
          @click="toggleLayout(0)"
          v-text="4"
        />

        <button
          :aria-label="$string('accessibility.collection.change_layout')"
          type="button"
          class="main-collection__layout-toggle text-desktop-regular"
          :class="currentLayout === 1 && 'main-collection__layout-toggle--active'"
          @click="toggleLayout(1)"
          v-text="8"
        />
      </div>

      <!-- Filter toggle -->
      <div
        v-if="!collection.hideProductGrid"
        class="main-collection__filter-toggle-container col l3"
      >
        <button
          type="button"
          class="
            main-collection__filter-toggle
            button--text
            text-desktop-14-desktop
            text-mobile-14-mobile
          "
          @click="openFilters"
        >
          <icon-plus />
          <span
            class="main-collection__filter-text"
            v-text="$string('collection.open_filters')"
          />
        </button>

        <button
          v-if="selectedFilters.length"
          type="button"
          class="main-collection__filter-reset-text
            button--text
            text-desktop-14-desktop
            text-mobile-14-mobile"
          @click="resetAllFilters"
          v-text="$string('collection.reset_filters')"
        />
      </div>
    </div>
    <template v-if="!collection.hideProductGrid && !hideCollection">
      <div
        ref="container"
        class="main-collection__grid"
        :class="`main-collection__grid--${layout[currentLayout]}`"
      >
        <template
          v-for="(product, index) of collectionProducts"
          :key="`product-card-${section.id}-${index}`"
        >
          <product-card
            :collection="collection.handle"
            :disable-custom-tag="collection.metaDisableCustomTag"
            :data-product-page="Math.ceil((index + 1) / section.productLimit)"
            :filters-selected="Boolean(selectedFilters.length)"
            :index="index"
            :layout="layout[currentLayout]"
            :product="product"
            :show-flat-image="collection.productFlatImage"
            :product-order="(index + 1)"
            :hidden="showViewMoreButton && index >= visibleProducts"
            :hide-color-swatches="collection.hideProductColorSwatches"
          />
        </template>

        <template
          v-for="(item, index) of blocks"
          :key="`product-grid__item-${index}`"
        >
          <template v-if="item?.blockSettings && Boolean(collectionProducts.length)">
            <!-- Wrap in anchor if URL exists -->
            <a
              v-if="item.blockSettings.url"
              v-show="showPromoBlock(item)"
              class="main-collection__block"
              :class="getBlockClass(item)"
              :style="getBlockStyles(item)"
              :href="item.blockSettings.url"
            >
              <media-content
                :component-settings="{
                  layout: item.blockSettings.mediaLayout,
                  hasContent: false,
                  autoplay: true,
                  muted: true,
                  loop: true
                }"
                block-class="main-collection"
                :content-placement="item.desktopMediaSettings.desktopFullRow ? 'full' : 'half'"
                :mobile-media-settings="item.mobileMediaSettings"
                :desktop-media-settings="item.desktopMediaSettings"
              />
              <div
                v-if="item.blockSettings.text"
                class="main-collection__block-text"
              >
                <icon-chevron-right />
                <span
                  class="text-regular text-desktop-small text-mobile-small"
                  v-text="item.blockSettings.text"
                />
              </div>
            </a>

            <!-- Regular div if no URL -->
            <div
              v-else
              class="main-collection__block"
              :class="getBlockClass(item)"
              :style="getBlockStyles(item)"
            >
              <media-content
                :component-settings="{
                  layout: item.blockSettings.mediaLayout,
                  hasContent: false,
                  autoplay: true,
                  muted: true,
                  loop: true
                }"
                block-class="main-collection"
                :content-placement="item.desktopMediaSettings.desktopFullRow ? 'full' : 'half'"
                :mobile-media-settings="item.mobileMediaSettings"
                :desktop-media-settings="item.desktopMediaSettings"
              />
              <div
                v-if="item.blockSettings.text"
                class="main-collection__block-text"
              >
                <p>
                  <span
                    class="text-regular text-desktop-small text-mobile-small"
                    v-text="item.blockSettings.text"
                  />
                </p>
              </div>
            </div>
          </template>
        </template>
      </div>

      <btn
        v-if="showViewMoreButton"
        class="main-collection__view-more"
        block-class="main-collection"
        :label="$string('collection.view_more')"
        :show-text-icon="false"
        type="button"
        @click="handleViewMore"
      />

      <depict-collection
        :merchant="section.merchant"
        :collection="collection.handle"
        :limit="section.productLimit"
        :offset="offset"
        :cursor="cursor"
        :selected-filters="selectedFilters"
        @collection-updated="handleCollectionProducts"
      />
    </template>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Collection page.
 *
 * @param {Number} collection.breadcrumbs - Breadcrumbs from metafields
 * @param {Boolean} collection.hideBreadcrumbs - Hide breadcrumbs.
 * @param {Number} collection.handle - Collection handle
 * @param {Number} collection.id - Collection ID.
 * @param {Boolean} collection.hideHeader - Hide header section.
 * @param {String} collection.metaTitleDesktop - Desktop title from metafields.
 * @param {String} collection.metaTitleMobile - Mobile title from metafields.
 * @param {Object} collection.navigation - Collection navigation
 * @param {Number} collection.productLimit - Products per page.
 * @param {Boolean} collection.productFlatImage - Product flat image.
 * @param {String} collection.products - Collection products
 * @param {Boolean} collection.hideProductGrid - Hide default product grid.
 * @param {Boolean} collection.hideProductColorSwatches - Hide color swatches.
 * @param {String} collection.styles - Collection section styles
 * @param {String} collection.title - Collection title.
 * @param {Boolean} collection.hideTitle - Hide collection title.
 * @param {String} collection.description - Collection description.
 * @param {String} collection.descriptionAlignMentMobile - Description align.
 * @param {Boolean} collection.hideDescription - Hide Collection description.
 * @param {Array} collection.hideVariantsProductTypes - Hide variants on type
 * @param {Number|Boolean} collection.visibleProductsDesktop - Visible products
 * @param {Number|Boolean} collection.visibleProductsMobile - Visible products
 * @param {String} collection.url - Collection url
 * @param {String} section - Collection section settings.
 *
 */
import { mapActions, mapState } from 'vuex'
import { isVisible, throttle } from '~/helpers/general'
import { formatDepictFilters, formatDepictProduct } from '~/helpers/format'

import Overlay from '~global/overlay/overlay'
import DepictCollection from '~global/depict/depict-collection'
import BreadCrumbs from '~async/bread-crumbs/bread-crumbs'
import FilterList from '~global/filter-list/filter-list'
import FilterTabs from '~global/filter-tabs/filter-tabs'

import MediaContent from '~async/media-content/media-content'
import ProductCard from '~async/product-card/product-card'
import Btn from '~global/btn/btn'

import IconPlus from '~icons/misc/plus-new.svg'
import PrivateCollection from '~async/private-collection/private-collection.vue'

export default {
  name: 'MainCollection',

  components: {
    PrivateCollection,
    DepictCollection,
    BreadCrumbs,
    FilterList,
    FilterTabs,
    ProductCard,
    Btn,
    Overlay,
    IconPlus,
    MediaContent,
  },

  props: {
    collection: {
      type: Object,
      default: () => ({
        id: '',
        hideHeader: false,
        title: '',
        hideTitle: false,
        description: '',
        descriptionAlignMentMobile: '',
        hideDescription: false,
        breadcrumbs: '',
        hideBreadcrumbs: false,
        handle: '',
        metaDisableCustomTag: false,
        metaTitleDesktop: {},
        metaTitleMobile: {},
        navigation: {},
        url: '',
        products: [],
        productLimit: '',
        productFlatImage: '',
        hideProductGrid: false,
        hideProductColorSwatches: false,
        visibleProductsDesktop: false,
        visibleProductsMobile: false,
        styles: {},
      }),
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
        productLimit: 0,
        merchant: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    locked: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      allProductsLoaded: false,
      collectionProducts: [],
      currentLayout: 0,
      endOfGrid: false,
      filters: [],
      hideFooter: false,
      initialPageNumber: 0,
      initialLoad: true,
      isScrolling: false,
      layout: ['medium', 'overview', 'large'],
      meta: {},
      noResults: false,
      offset: 0,
      cursor: null,
      pageNumber: 1,
      scrollPosition: 0,
      searchResults: [],
      selectedFilters: [],
      showCollectionFilters: false,
      resizeState: false,
      tabExpanded: false,
      topProductCard: null,
      leftAlignNavigation: false,
      hideCollection: false,
      showViewMoreButton: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute background color based on currently selected layout.
     * @returns {String}
     */
    backgroundColor() {
      const layout = this.layout[this.currentLayout]

      switch (layout) {
        case 'overview':
          return this.collection.styles.smallGridBackground

        case 'medium':
          return this.collection.styles.mediumGridBackground

        case 'large':
        default:
          return this.collection.styles.largeGridBackground
      }
    },

    /**
     * Compute filter background color based on collection metafield.
     * @returns {String}
     */
    filterBackground() {
      return this.collection.styles.filterBackground
    },

    /**
     * Compute filter content color based on collection metafield.
     * @returns {String}
     */
    filterContentColour() {
      return this.collection.styles.filterContent === '' ? '' : `main-collection__header--${this.collection.styles.filterContent}`
    },

    /**
     * Compute content color based on currently selected layout.
     * @returns {String}
     */
    contentColor() {
      const layout = this.layout[this.currentLayout]

      switch (layout) {
        case 'overview':
          return this.collection.styles.smallGridContent

        case 'medium':
          return this.collection.styles.mediumGridContent

        case 'large':
        default:
          return this.collection.styles.largeGridContent
      }
    },

    /**
     * Compute layout icon based on currently selected layout.
     * @returns {String}
     */
    layoutIcon() {
      const layout = this.layout[this.currentLayout]

      if (this.isMobile) {
        switch (layout) {
          case 'overview':
            return 'icon-layout-overview'

          case 'medium':
            return 'icon-layout-two'

          case 'large':
          default:
            return 'icon-layout-one'
        }
      }

      switch (layout) {
        case 'overview':
          return 'icon-layout-two'

        case 'medium':
          return 'icon-layout-overview'

        case 'large':
        default:
          return 'icon-layout-four'
      }
    },

    /**
     * Compute current layout icon.
     * @returns {String}
     */
    currentLayoutText() {
      const layout = this.layout[this.currentLayout]

      switch (layout) {
        case 'overview':
          return this.$string('collection.overview_layout')

        case 'medium':
          return this.$string('collection.medium_layout')

        case 'large':
        default:
          return this.$string('collection.large_layout')
      }
    },

    /**
     * Compute current layout icon.
     * @returns {String}
     */
    currentLayoutIcon() {
      const layout = this.layout[this.currentLayout]

      switch (layout) {
        case 'overview':
          return 'icon-layout-overview'

        case 'medium':
          return 'icon-layout-four'

        case 'large':
        default:
          return 'icon-layout-two'
      }
    },

    /**
     * Returns collection layout text.
     * @returns {String}
     */
    layoutText() {
      return this.$string('collection.layout')
    },

    /**
     * Compute filter tab title string
     * @returns {String}
     */
    tabTitle() {
      return this.$string('collection.show_products', {
        replace: {
          num: this.meta?.totalResultsFound ? this.meta.totalResultsFound : '',
        },
      })
    },

    /**
     * Compute collection meta visibility based on scroll position and filters.
     * @returns {Boolean}
     */
    showCollectionMeta() {
      return (
        (this.isMobile && !this.tabExpanded) ||
        (!this.isMobile && !this.showCollectionFilters)) &&
        !this.isScrolling &&
        (this.collection.metaTitleDesktop || this.collection.metaTitleMobile)
    },

    /**
     * Returns visible products based on current device.
     * @returns {Number|Boolean}
     */
    visibleProducts() {
      return this.isMobile
        ? this.collection.visibleProductsMobile
        : this.collection.visibleProductsDesktop
    },
  },

  watch: {

    /**
     * Watch selected filters to reset offset and page number.
     * - Scrolls to top of page and resets page number to 1
     */
    selectedFilters() {
      // window.scrollTo(0, 0)
      this.pageNumber = 1
      this.offset = 0
      this.cursor = null
    },

    /**
     * Watch page number to update URL params.
     */
    pageNumber() {
      // this.updateUrlPageParams(this.pageNumber)
    },

    /**
     * Watch number of products to update view more button.
     */
    collectionProducts() {
      this.checkViewMoreButton()
    },
  },

  mounted() {
    if (this.collection.hideProductGrid) {
      return
    }

    this.getLayout()
    this.handleLayoutResize()
    this.clearPreviousProduct()
    this.loadProductsFromPageNumber()
    // this.scrollToSelected()
    this.setScrollEventListener()
    this.checkLeftAlignNavigation()
  },

  methods: {

    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    openFilters() {
      this.toggleOverlay({
        namespace: 'CollectionFilters',
        ignoreDismissed: true,
        component: 'main-collection',
      })
    },

    /**
     * Sets the scroll event listener.
     */
    setScrollEventListener() {
      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), 16),
      )
    },

    /**
     * Fetches selected layout from session storage if available
     */
    getLayout() {
      if (!sessionStorage.getItem('layout')) {
        return
      }

      this.currentLayout = Number(sessionStorage.getItem('layout'))
    },

    /**
     * Toggles collection grid layout.
     */
    toggleLayout(newLayout) {
      this.resizeState = true

      if (this.isMobile) {
        switch (this.currentLayout) {
          case 0:
            this.currentLayout = 1
            break
          case 1:
            this.currentLayout = 2
            break
          default:
            this.currentLayout = 0
            break
        }
      } else {
        this.currentLayout = newLayout
      }
      sessionStorage.setItem('layout', this.currentLayout)
      this.checkViewMoreButton()
      this.handleGtm('layout')
    },

    /**
     * Wether to show media block based on current layout, filter, media order
     * and visibility of view more button.
     * @param {Number} mediaOrder - Order of media block.
     * @returns {Boolean}
     */
    showMediaBlock(mediaOrder) {
      return this.layout[this.currentLayout] !== 'medium' ||
        Boolean(this.selectedFilters.length) ||
        (this.showViewMoreButton && mediaOrder > this.visibleProducts)
    },

    showPromoBlock(item) {
      return item.blockSettings.mediaOrder <= this.collectionProducts.length
    },

    getBlockClass(item) {
      return [
        {
          [this.$class('hidden')]: this.isMobile
            ? item.blockSettings.hideMobile
            : item.blockSettings.hideDesktop,
        },
        this.getDesktopBleedClass(item),
        this.getMobileRowClass(item),
        this.getDesktopRowClass(item),
        this.showMediaBlock(item.blockSettings.mediaOrder) && [this.$class('hidden')],
      ]
    },

    getBlockStyles(item) {
      return {
        '--columns-mobile': item.mobileMediaSettings.mobileMediaWidth,
        '--columns-desktop': item.desktopMediaSettings.desktopMediaWidth,
        '--rows-desktop': item.desktopMediaSettings.desktopMediaHeight,
        '--order': `${item.blockSettings.mediaOrder - 1}`,
      }
    },

    getDesktopBleedClass(item) {
      return item.desktopMediaSettings.desktopMediaWidth === 'full-bleed' &&
        'main-collection__block--full-bleed-desktop'
    },

    getMobileRowClass(item) {
      return item.mobileMediaSettings.mobileFullRow &&
        'main-collection__block--full-row-mobile'
    },

    getDesktopRowClass(item) {
      return item.desktopMediaSettings.desktopFullRow &&
        'main-collection__block--full-row-desktop'
    },

    checkViewMoreButton() {
      if (!this.visibleProducts) {
        return
      }

      const currentUrl = new URL(window.location.href)
      const params = new URLSearchParams(currentUrl.search)

      /**
       * If the number of products is less than or equal to the visible products
       * or the view more button has been pressed
       * or if the current grid layout is 8 per row, hide the view more button.
       */
      if (this.collectionProducts.length <= this.visibleProducts ||
        params.get('view_more') === 'true' ||
        this.currentLayout === 1
      ) {
        this.showViewMoreButton = false
      } else {
        this.showViewMoreButton = true
      }
    },

    handleViewMore() {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.append('view_more', 'true')
      window.history.replaceState({}, '', currentUrl)

      this.showViewMoreButton = false
      this.handleGtm('view-more')
    },

    /**
     * @param {String} item - Item to push to dataLayer.
     */
    handleGtm(item) {
      const dataLayer = window.dataLayer || []

      if (item === 'layout') {
        let layoutValue

        switch (this.currentLayout) {
          case 0:
            layoutValue = 4
            break
          case 1:
            layoutValue = 8
            break
          case 2:
            layoutValue = 2
            break
        }

        dataLayer.push({
          event: 'product_grid_view',
          productGridView: {
            url: document.location.pathname,
            view: layoutValue,
          },
        })
      } else if (item === 'view-more') {
        dataLayer.push({
          event: 'collection_view_more_click',
          collectionViewMoreClick: {
            url: document.location.pathname,
            handle: this.collection.handle,
          },
        })
      }
    },

    /**
     * Sets a resize observer that listens for the grid layout being resized
     * and handles offsetting the page position being scrolled away by the
     * page height being increased.
     */
    handleLayoutResize() {
      // Make sure the header is collapsed
      if (window.scrollY > this.$refs.container.offsetTop) {
        document.documentElement.setAttribute('data-scrolldir', 'down')
      }
    },

    /**
     * If the page is entered other than via a product, reset the last selected
     * product in session storage to prevent weird random scrolling events.
     */
    clearPreviousProduct() {
      const previousUrl = sessionStorage.getItem('previousUrl')
      if (!previousUrl?.includes('/products/')) {
        sessionStorage.removeItem('selected_product_id')
      }
    },

    /**
     * Loads products corresponding to page number on page load.
     */
    loadProductsFromPageNumber() {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)

      if (!params.get('page')) {
        return
      }

      const pageNumber = Number(params.get('page'))

      for (let index = 1; index < pageNumber; index++) {
        setTimeout(() => {
          this.offset = Number(this.section.productLimit * index)
        }, 500)
      }

      this.initialPageNumber = pageNumber
    },

    /**
     * If a product has been previously selected scroll to it on load.
     * If no product can be found, scroll to most recent page
     * If no page is found, scroll to top of screen.
     */
    async scrollToSelected() {
      const productId = sessionStorage.getItem('selected_product_id')

      await new Promise((resolve) => {
        const scrollInterval = window.setInterval(() => {
          if (this.initialLoad) {
            return
          }

          window.clearInterval(scrollInterval)
          resolve()
        }, this.$timing('quick'))
      })

      if (!productId) {
        if (this.initialPageNumber <= 1) {
          return
        }

        let topCard
        let elapsedTime = 0

        await new Promise((resolve) => {
          const scrollInterval = window.setInterval(() => {
            topCard = document.querySelector(`[data-product-page='${this.initialPageNumber}']`)

            if (!topCard && elapsedTime < 2000) {
              elapsedTime += this.$timing('quick')
              return
            }

            window.clearInterval(scrollInterval)
            resolve()
          }, this.$timing('quick'))
        })

        if (topCard) {
          topCard.scrollIntoView(
            {
              behavior: 'smooth',
            },
          )
          return
        }

        window.scrollTo(0, 0)
        return
      }

      let productCard
      let elapsedTime = 0
      await new Promise((resolve) => {
        const scrollInterval = window.setInterval(() => {
          productCard = document.getElementById(`product-card-${productId}`)

          if (!productCard && elapsedTime < 2000) {
            elapsedTime += this.$timing('quick')
            return
          }

          window.clearInterval(scrollInterval)
          resolve()
        }, this.$timing('quick'))
      })

      sessionStorage.removeItem('selected_product_id')

      if (!productCard) {
        window.scrollTo(0, 0)
        return
      }

      productCard.scrollIntoView(
        {
          behavior: 'smooth',
        },
      )
    },

    /**
     * Finds the top most on-screen product card, reads its product page
     * and updates the URL search params accordingly.
     */
    getCurrentProductPage() {
      const cards = [...document.querySelectorAll('.product-card')]
      const topCard = cards.find(card => isVisible(card))
      this.topProductCard = topCard

      if (!topCard) {
        return
      }

      const page = Number(topCard.dataset.productPage)

      if (page === this.pageNumber) {
        return
      }

      this.pageNumber = page
    },

    /**
     * Updates url param with current page number
     * @param {Number} pageNumber - number of current page
     */
    updateUrlPageParams(pageNumber) {
      const currentUrl = new URL(window.location)
      currentUrl.searchParams.set('page', pageNumber)

      window.history.replaceState({}, '', currentUrl)
    },

    /**
     * Updates url param with selected filter
     * @param {String} filter - Key of selected filter
     * @param {String} value - Value of selected filter
     */
    updateUrlFilterParams(filter, value) {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)

      if (params.get(filter)) {
        const currentParams = params.get(filter)
        const updatedParams = [
          ...currentParams.split(','),
          value,
        ].join(',')

        currentUrl.searchParams.set(filter, updatedParams)
      } else {
        currentUrl.searchParams.set(filter, value)
      }

      window.history.replaceState({}, '', currentUrl)
    },

    /**
     * Deletes selected filter from params
     * @param {String} filter - Key of selected filter
     * @param {String} value - Value of selected filter
     */
    deleteUrlFilterParams(filter, value) {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)

      if (!params.get(filter)) {
        return
      }

      if (value) {
        const currentParams = params.get(filter)
        // remove selected value from params
        const updatedParams = currentParams.split(',').filter(param => param !== value)

        if (updatedParams.length) {
          currentUrl.searchParams.set(filter, updatedParams.join(','))
        } else {
          // delete all filter params if no value is selected
          currentUrl.searchParams.delete(filter)
        }
      } else {
        // delete all filter params if no value is inputed
        currentUrl.searchParams.delete(filter)
      }

      window.history.replaceState({}, '', currentUrl)
    },

    /**
     * Update selected filters from URL parameter on page load.
     * @param {Object} filter - filter object from Depict
     */
    updateSelectedFiltersFromUrl(filter) {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)

      const currentParams = params.get(filter.key)

      if (!currentParams) {
        return
      }

      const paramOptions = currentParams.split(',').map((param) => decodeURIComponent(param))
      const filterArray = [...this.selectedFilters]

      filterArray.push({
        key: filter.key,
        values: paramOptions,
        op: filter.op,
        settings: {
          singleSelect: false,
        },
      })

      this.selectedFilters = filterArray
    },

    /**
     * Check if filter key contains values of selected key
     * @param {String} key - Key value of selected filter.
     * @returns {Boolean}
     */
    filterContainsSelected(key) {
      const currentFilter = this.selectedFilters?.find(
        (filter) => filter.key === key,
      )
      return Boolean(currentFilter?.values?.length)
    },

    /**
     * Updates selected filters based on selected checkbox
     * @param {Object} option - Option object containing event, key and value.
     */
    handleFilterSelect(option) {
      const { event, key, value, op } = option
      const filterArray = [...this.selectedFilters]

      // Check if filter object exists in selected filters
      const currentFilter = filterArray.find((filter) => filter.key === key)

      if (currentFilter) {
        // Check if checkbox is selected and the value doesn't already exist
        const isChecked = event.target.checked
        const valueExists = currentFilter.values.includes(value)

        if (isChecked && !valueExists) {
          currentFilter.values.push(value)
          this.updateUrlFilterParams(key, value)
        } else if (!isChecked && valueExists) {
          const valueIndex = currentFilter.values.indexOf(value)
          currentFilter.values.splice(valueIndex, 1)
          this.deleteUrlFilterParams(key, value)

          // Delete filter object from array if no value is selected
          if (!currentFilter.values.length) {
            const filterIndex = filterArray.indexOf(currentFilter)
            filterArray.splice(filterIndex, 1)
          }
        }
      } else {
        // Create new filter object in selected filters
        filterArray.push({
          key,
          values: [value],
          op,
          settings: {
            singleSelect: false,
          },
        })
        this.updateUrlFilterParams(key, value)
      }

      // Reassign selectedFilters to trigger Vue reactive state
      this.selectedFilters = filterArray
    },

    /**
     * Clears the current selected filter array
     * @param {String} key - Key value of selected filter.
     */
    resetFilter(key) {
      const filterArray = [...this.selectedFilters]
      const currentFilter = filterArray.find((filter) => filter.key === key)
      const filterIndex = filterArray.indexOf(currentFilter)

      filterArray.splice(filterIndex, 1)
      this.selectedFilters = filterArray
      this.$refs[key]?.forEach((ref) => {
        ref.resetComponentData(key)
      })
      this.deleteUrlFilterParams(key)
    },

    /**
     * Clears all filters .
     */
    resetAllFilters() {
      const filterArray = [...this.selectedFilters]
      const filterKeys = filterArray.map((filter) => {
        return filter.key
      })

      filterKeys.forEach((key) => {
        this.resetFilter(key)
      })
    },

    /**
     * Function to handle scroll events on collection page
     * - Handles infinite scroll for grid pagination
     * - Handles updating current page param
     */
    handleScroll() {
      this.getCurrentProductPage()

      const container = this.$refs.container

      if (!container) {
        return
      }

      const containerRect = container.getBoundingClientRect()
      const endOfGrid = (containerRect.bottom - (window.innerHeight * 1.25)) <= 0
      const scrollingUp = this.scrollPosition > containerRect.top

      this.isScrolling = containerRect.top < 0

      if (scrollingUp && endOfGrid && !this.showMore) {
        this.updateOffset()
      }

      // If the collection grid is the same height as the window, hide
      // the footer when the collection has scrolled 10% away from the
      // bottom of the screen. Else, hide the footer when the collection
      // has reached the bottom of the screen
      const footerEnd = containerRect.height <= window.innerHeight
        ? containerRect.bottom <= (window.innerHeight * 0.9)
        : containerRect.bottom <= window.innerHeight

      if (this.allProductsLoaded && footerEnd) {
        this.hideFooter = true
      } else {
        this.hideFooter = false
      }

      this.scrollPosition = containerRect.top
    },

    /**
     * Updates offset value based on fetched product count
     */
    updateOffset() {
      const { noOfResults, offset, cursor, totalResultsFound } = this.meta
      const fetchedProductCount = noOfResults + offset

      if (fetchedProductCount >= totalResultsFound) {
        this.allProductsLoaded = true
        return
      }

      this.cursor = cursor
      this.offset = Number(fetchedProductCount)
    },

    /**
     * Handles collection products results and filters from Depict response
     * @param {Object} response - Response object from Depict API.
     */
    handleCollectionProducts(response) {
      const { data, action } = response

      const products = data?.displays

      this.meta = {
        noOfResults: products.length,
        offset: data.offset,
        cursor: data.cursor,
        totalResultsFound: data.n_hits,
      }

      if (isNaN(this.meta.offset)) {
        this.meta.offset = 0
      }

      this.filters = formatDepictFilters(data?.filters, this.selectedFilters)

      const formattedProducts = products?.map((product) => {
        return formatDepictProduct(product)
      })

      if (action === 'offset') {
        // Append new product results to existing array
        this.collectionProducts = [...this.collectionProducts, ...formattedProducts]
      } else {
        this.collectionProducts = formattedProducts
      }

      if (this.initialLoad) {
        this.filters.forEach((filter) => {
          this.updateSelectedFiltersFromUrl(filter)
        })
      }

      this.cursor = response.cursor

      this.initialLoad = false
    },

    checkLeftAlignNavigation() {
      const navigation = this.$refs.navigation

      if (!navigation) {
        return
      }

      if (navigation.offsetWidth > 380) {
        this.leftAlignNavigation = true
      }
    },
  },
}
</script>

<style lang="scss">
@import "./main-collection";
</style>
